import { checkReopenTime } from "@shared/common/component-manager/expiration";
import { getLocalData } from "@shared/common/component-manager/local-storage";
import { checkSchedule } from "@shared/common/domain/schedule";
import type {
  ICampaignListJson,
  ICampaignListJsonCampaignItem,
  ICampaignListJsonDataSource,
} from "@shared/common/types/campaign";
import type {
  PartialData,
  PartialHmallLiveBroadProductListData,
} from "@shared/common/types/data-source";
import {
  type DataMapItem,
  type GetCartInfoRes,
  type GetCouponInfoRes,
  type IAnalyticsBootResponse,
} from "@shared/common/types/sdk";
import type { IEngageableCampaign } from "@shared/common/types/target-segment";
import { separateResults } from "@shared/common/utils/helper";
import { isTimeInRange } from "@shared/common/utils/timer";
import Cookies from "js-cookie";
import { getComponentId, getDevice } from "./component-manager";
import { fetchSocialProof } from "./datasource";
import { getJson, getVideoJson } from "./json";
import type { IVisitorDataLoader } from "./sites/visitor-data-loader";
import { State } from "./state";
import { checkTarget } from "./target";
import type { MountOptions } from "./type";
import asyncFilter from "./util/async-filter";
import { logError } from "./util/common";
import { getLocalStorage, getSessionStorage } from "./util/target";
import { getCampaignHistories } from "./visit-counts/campaign-history";
import { checkFrequencyCapping } from "./visit-counts/frequency-capping";

export const createInitialUserData = () => ({
  isLogin: new State<boolean>(),
  loginId: new State<string | null>(),
  name: new State<string | null>(),
  email: new State<string | null>(),
  cart: new State<GetCartInfoRes>(),
  wishlistCount: new State<number | null>(),
  coupon: new State<GetCouponInfoRes>(),
  engageableCampaigns: new State<IEngageableCampaign[]>(),
});

export type UserData = ReturnType<typeof createInitialUserData>;

export const getMountingCampaigns = async ({
  campaignListJsonState,
  bootResState,
  options,
  userData,
  customData,
  visitorDataLoaderState,
}: {
  campaignListJsonState: State<ICampaignListJson>;
  bootResState: State<IAnalyticsBootResponse>;
  options?: MountOptions;
  userData: UserData;
  customData: Promise<Record<string, unknown>>;
  visitorDataLoaderState: State<IVisitorDataLoader>;
}) => {
  const currentDevice = getDevice();

  const campaignListJson = await campaignListJsonState.get();
  const { isNewUser, location } = await bootResState.get();
  const siteId = campaignListJson.site.publicId;

  return asyncFilter(campaignListJson.campaigns, async (campaign) => {
    if (
      (campaign.type !== "POPUP" && campaign.type !== "POPUP_GROUP") ||
      campaign.parentPublicId
    ) {
      return false;
    }

    // 마운트 제외할 캠페인 아이디 체크
    if (
      options?.excludedCampaignIds !== undefined &&
      options.excludedCampaignIds.includes(campaign.publicId)
    ) {
      return false;
    }

    // 캠페인 아이디 체크
    if (options?.id !== undefined && campaign.publicId !== options.id) {
      return false;
    }

    // 체크 1 - 디바이스
    const supportedDevices = campaign.supportedDevicesFromDocumentData;
    if (
      supportedDevices &&
      supportedDevices.length > 0 &&
      !supportedDevices.includes("ALL") &&
      !supportedDevices.includes(currentDevice) &&
      options?.ignoreDevice !== true
    ) {
      return false;
    }

    // 체크 2 - 노출 스케줄
    const checkScheduleResult = checkSchedule({
      schedule: campaign.schedule,
      currentDate: new Date(),
      currentDevice,
    });

    if (!options?.ignoreCheckSchedule && checkScheduleResult.type === "FAIL") {
      return false;
    }

    // 체크 3 - 노출 대상
    const geolocationValue = {
      postal: location.postal,
      region: location.region,
      city: location.city,
      country: location.country,
    };

    const ipValue = location.ip;

    const checkTargetResult = await checkTarget(
      campaign.target,
      {
        url: campaignListJson.site.url,
        siteInfo: campaignListJson.site.siteInfo,
        siteType: campaignListJson.site.siteType,
      },
      {
        pathname: window.location.pathname,
        title: document.title,
        url: new URL(window.location.href),
        html: document.documentElement,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        language: navigator.language,
        queryString: window.location.search,
        localStorage: getLocalStorage(),
        sessionStorage: getSessionStorage(),
        cookie: Cookies.get(),
        utm: sessionStorage.getItem("_cnbutm"),
        geolocation: geolocationValue,
        ip: ipValue,
        windowObj: window,
        isNewUser,
        user: userData,
        customData,
        currentDate: new Date(),
        campaignHistory: getCampaignHistories()[campaign.publicId],
        getCurrentPageDetails: async () => {
          const visitorDataLoader = await visitorDataLoaderState.get();
          return visitorDataLoader.getCurrentPageDetails?.() ?? {};
        },
        checkUserIsEngageable: async () => {
          const engageableCampaigns = await userData.engageableCampaigns.get();
          return engageableCampaigns.some(
            (engageableCampaign) =>
              engageableCampaign.campaignId === campaign.publicId
          );
        },
        dataSources: campaign.dataSources,
        getData: async <T extends PartialData["data"]>(
          dataSourceId: string
        ) => {
          const dataSource = campaign.dataSources.find(
            (dataSource) => dataSourceId === dataSource.id
          );
          if (!dataSource) {
            return undefined;
          }
          return getDataSourceData({
            dataSource,
            visitorDataLoaderState,
            siteId,
          }) as Promise<T | undefined>;
        },
      }
    );

    if (!options?.ignoreCheckTarget && checkTargetResult.type === "FAIL") {
      return false;
    }

    // 체크 4 - 다시 보지 않기 (팝업 중복 노출 허용 시에만)
    if (
      !options?.ignoreExpired &&
      !campaignListJson.site.option?.showOnlyOnePopupPerPage
    ) {
      try {
        if (
          !checkReopenTime({
            componentId: getComponentId(siteId, campaign.publicId),
            localData: getLocalData(),
          })
        ) {
          return false;
        }
      } catch (e) {
        logError(
          "브라우저 설정으로 인해 일정 시간 닫기 기능을 사용할 수 없습니다."
        );
      }
    }

    // 체크 5 - 피로도 확인
    if (
      !checkFrequencyCapping(campaign.display, {
        campaignPublicId: campaign.publicId,
        campaignUpdatedAt: campaign.meta?.updatedAt ?? new Date().toISOString(),
      })
    ) {
      return false;
    }

    return true;
  });
};

export const getChildrenCampaigns = async ({
  campaignListJsonState,
  bootResState,
  options,
  userData,
  customData,
  visitorDataLoaderState,
}: {
  campaignListJsonState: State<ICampaignListJson>;
  bootResState: State<IAnalyticsBootResponse>;
  options?: MountOptions;
  userData: UserData;
  customData: Promise<Record<string, unknown>>;
  visitorDataLoaderState: State<IVisitorDataLoader>;
}) => {
  const currentDevice = getDevice();
  const childrenCampaignSlots: Record<string, number[]> = {};

  const campaignListJson = await campaignListJsonState.get();
  const { isNewUser, location } = await bootResState.get();

  const siteId = campaignListJson.site.publicId;

  const childrenCampaigns = await asyncFilter(
    campaignListJson.campaigns,
    async (campaign) => {
      if (!campaign.parentPublicId) {
        return false;
      }
      // 체크 1 - 디바이스
      const supportedDevices = campaign.supportedDevicesFromDocumentData;
      if (
        supportedDevices &&
        supportedDevices.length > 0 &&
        !supportedDevices.includes("ALL") &&
        !supportedDevices.includes(currentDevice) &&
        options?.ignoreDevice !== true
      ) {
        return false;
      }

      // 체크 2 - 노출 스케줄
      const checkScheduleResult = checkSchedule({
        schedule: campaign.schedule,
        currentDate: new Date(),
        currentDevice,
      });
      if (
        !options?.ignoreCheckSchedule &&
        checkScheduleResult.type !== "SUCCESS"
      ) {
        return false;
      }

      // 체크 3 - 노출 대상
      const geolocationValue = {
        postal: location.postal,
        region: location.region,
        city: location.city,
        country: location.country,
      };

      const ipValue = location.ip;

      const checkTargetResult = await checkTarget(
        campaign.target,
        {
          url: campaignListJson.site.url,
          siteInfo: campaignListJson.site.siteInfo,
          siteType: campaignListJson.site.siteType,
        },
        {
          pathname: window.location.pathname,
          title: document.title,
          url: new URL(window.location.href),
          html: document.documentElement,
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          language: navigator.language,
          queryString: window.location.search,
          localStorage: getLocalStorage(),
          sessionStorage: getSessionStorage(),
          cookie: Cookies.get(),
          utm: sessionStorage.getItem("_cnbutm"),
          geolocation: geolocationValue,
          ip: ipValue,
          windowObj: window,
          isNewUser,
          user: userData,
          customData,
          currentDate: new Date(),
          campaignHistory: getCampaignHistories()[campaign.publicId],
          getCurrentPageDetails: async () => {
            const visitorDataLoader = await visitorDataLoaderState.get();
            return visitorDataLoader.getCurrentPageDetails?.() ?? {};
          },
          checkUserIsEngageable: async () => {
            const engageableCampaigns =
              await userData.engageableCampaigns.get();
            return engageableCampaigns.some(
              (engageableCampaign) =>
                engageableCampaign.campaignId === campaign.publicId
            );
          },
          dataSources: campaign.dataSources,
          getData: async <T extends PartialData["data"]>(
            dataSourceId: string
          ) => {
            const dataSource = campaign.dataSources.find(
              (dataSource) => dataSourceId === dataSource.id
            );
            if (!dataSource) {
              return undefined;
            }

            return getDataSourceData({
              dataSource,
              visitorDataLoaderState,
              siteId,
            }) as Promise<T | undefined>;
          },
        }
      );

      if (!options?.ignoreCheckTarget && checkTargetResult.type === "FAIL") {
        return false;
      }

      if (
        checkScheduleResult.type === "SUCCESS" ||
        options?.ignoreCheckSchedule
      ) {
        if (checkScheduleResult.type === "SUCCESS") {
          const slot = checkScheduleResult.slots;

          childrenCampaignSlots[campaign.publicId] =
            slot.length === 0 ? [9999] : slot;
        } else {
          childrenCampaignSlots[campaign.publicId] = [9999];
        }
      }

      return true;
    }
  );

  return {
    childrenCampaigns,
    childrenCampaignSlots,
  };
};

export const getDataMapItems = async (
  campaign: ICampaignListJsonCampaignItem,
  visitorDataLoaderState: State<IVisitorDataLoader>,
  siteId: string
): Promise<Array<DataMapItem>> => {
  const { fulfilled, rejected } = await separateResults(
    campaign.dataSources.map(
      async ({ alias, id, sourceParam, sourceType }): Promise<DataMapItem> => {
        if (!sourceType) {
          throw new Error("[codenbutter] sourceType is required");
        }

        switch (sourceType) {
          case "CODENBUTTER_VIDEO": {
            const dataJson = await Promise.all(
              (sourceParam?.data ?? []).map(async (data) => {
                const json = await getVideoJson(data);
                json.filename = data.filename;
                return json;
              })
            );

            return {
              alias,
              campaignPublicId: campaign.publicId,
              id,
              json: dataJson,
            };
          }

          case "CODENBUTTER_PRODUCTS":
          case "CODENBUTTER_INSTAGRAM":
          case "CODENBUTTER_CHILD_LAYOUTS":
          case "CODENBUTTER_CAMPAIGN_LIST": {
            return {
              alias,
              campaignPublicId: campaign.publicId,
              id,
              json: await getJson<PartialData["data"]>(
                `${CONFIG_BUCKET_BASE_URL}/data/${id}/data.json`
              ),
            };
          }

          case "HTTP": {
            switch (sourceParam?.type) {
              case "SOCIAL_PROOF": {
                const visitorDataLoader = await visitorDataLoaderState.get();
                const pageInfo = await visitorDataLoader.getCurrentPageInfo();

                return {
                  alias,
                  campaignPublicId: campaign.publicId,
                  id,
                  json:
                    pageInfo.type === "PRODUCT"
                      ? await fetchSocialProof({
                          sourceParam,
                          extraArgs: { siteId, productId: pageInfo.id },
                        })
                      : {},
                };
              }
              case "HMALL_BROAD_PRODUCT_LIST": {
                const json = await getJson<
                  PartialHmallLiveBroadProductListData["data"]
                >(`${CONFIG_BUCKET_BASE_URL}/data/${id}/data.json`);

                const currentTime = new Date().toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Asia/Seoul",
                  hour12: false,
                });

                const timeSafeProductList = json?.items.filter((item) =>
                  isTimeInRange(
                    item.brodStartTime,
                    item.brodEndTime,
                    currentTime
                  )
                );

                return {
                  alias,
                  campaignPublicId: campaign.publicId,
                  id,
                  json: { ...json, items: timeSafeProductList },
                };
              }
              case "POPULAR_PRODUCT_LIST": {
                return {
                  alias,
                  campaignPublicId: campaign.publicId,
                  id,
                  json: await getJson<PartialData["data"]>(
                    `${CONFIG_BUCKET_BASE_URL}/data/${id}/data.json`
                  ),
                };
              }
              default:
                throw new Error("Not implemented");
            }
          }
        }
      }
    )
  );

  rejected.forEach((error) => {
    logError(error);
  });

  return fulfilled;
};

export const getDataSourceData = async ({
  dataSource,
  visitorDataLoaderState,
  siteId,
}: {
  dataSource: ICampaignListJsonDataSource;
  visitorDataLoaderState: State<IVisitorDataLoader>;
  siteId: string;
}): Promise<PartialData["data"]> => {
  if (!dataSource.sourceType) {
    throw new Error("[codenbutter] sourceType is required");
  }

  switch (dataSource.sourceType) {
    case "CODENBUTTER_VIDEO": {
      return Promise.all(
        (dataSource.sourceParam?.data ?? []).map(async (data) => {
          const json = await getVideoJson(data);
          json.filename = data.filename;
          return json;
        })
      );
    }

    case "CODENBUTTER_PRODUCTS":
    case "CODENBUTTER_INSTAGRAM":
    case "CODENBUTTER_CHILD_LAYOUTS":
    case "CODENBUTTER_CAMPAIGN_LIST": {
      return getJson<PartialData["data"]>(
        `${CONFIG_BUCKET_BASE_URL}/data/${dataSource.id}/data.json`
      );
    }

    case "HTTP": {
      switch (dataSource.sourceParam?.type) {
        case "SOCIAL_PROOF": {
          const visitorDataLoader = await visitorDataLoaderState.get();
          const pageInfo = await visitorDataLoader.getCurrentPageInfo();

          return pageInfo.type === "PRODUCT"
            ? await fetchSocialProof({
                sourceParam: dataSource.sourceParam,
                extraArgs: { siteId, productId: pageInfo.id },
              })
            : {};
        }
        case "HMALL_BROAD_PRODUCT_LIST": {
          const json = await getJson<
            PartialHmallLiveBroadProductListData["data"]
          >(`${CONFIG_BUCKET_BASE_URL}/data/${dataSource.id}/data.json`);

          const currentTime = new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Asia/Seoul",
            hour12: false,
          });

          const timeSafeProductList = json?.items.filter((item) =>
            isTimeInRange(item.brodStartTime, item.brodEndTime, currentTime)
          );

          return { ...json, items: timeSafeProductList };
        }

        case "POPULAR_PRODUCT_LIST": {
          return getJson<PartialData["data"]>(
            `${CONFIG_BUCKET_BASE_URL}/data/${dataSource.id}/data.json`
          );
        }

        default:
          throw new Error("Not implemented");
      }
    }
  }
};

export type Mode = "easy_canvas_embedded" | "easy_canvas_popup";

export function getMode(): Mode | null {
  return localStorage.getItem("codenbutter_mode") as Mode | null;
}

export function setMode(mode: Mode) {
  localStorage.setItem("codenbutter_mode", mode);
}

export function removeMode() {
  localStorage.removeItem("codenbutter_mode");
}

export function getHighlightedCampaignPublicId(): string | null {
  return localStorage.getItem("codenbutter_highlighted_campaign_public_id");
}

export function setHighlightedCampaignPublicId(publicId: string) {
  localStorage.setItem("codenbutter_highlighted_campaign_public_id", publicId);
}

export function removeHighlightedCampaignPublicId() {
  localStorage.removeItem("codenbutter_highlighted_campaign_public_id");
}

/** 랜딩페이지에서 이지캔버스 데모 넘어왔는지 여부를 나타냄 */
export function getIsDemo(): boolean {
  return localStorage.getItem("codenbutter_is_demo") === "true";
}

/** 랜딩페이지에서 이지캔버스 데모 넘어왔는지 여부를 나타냄 */
export function setIsDemo(isDemo: boolean) {
  localStorage.setItem("codenbutter_is_demo", isDemo.toString());
}

export function removeIsDemo() {
  localStorage.removeItem("codenbutter_is_demo");
}

export const SELECTOR_ID_ATTR_NAME = "data-cb-embedded-selector-id";

export const TARGET_ELEMENT_ID_ATTR_NAME = "data-cb-target-element-id";

export const HORIZONTAL_ALIGN_ATTR_NAME = "data-cb-embedded-horizontal-align";

export const HOVERING_ATTR_NAME = "data-cb-embedded-hovering";

export const getPositionAttrName = (key: string) =>
  `data-cb-embedded-${key}-position`;

export const EVENT_DEDUPLICATION_ATTR_NAME = "data-event-deduplication";
